import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * StyledMarkdown - Renders markdown with VALORAMAP design system
 * Colors:
 * - Background: #0f0f23 (main), #1a1a2e (sections)
 * - Borders: #2a2a3e
 * - Accent: #00ff88 (electric blue)
 * - Text: #ffffff (primary), #4a4a5e (secondary)
 * - Positive: #00ff88, Negative: #ff4757, Warning: #ffa502
 */

const StyledMarkdown = ({ content, className = '' }) => {
  if (!content) return null;

  // Custom components for ReactMarkdown
  const components = {
    // H1 - Main title with blue border
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold text-white border-b-2 border-[#00ff88] pb-4 mb-6">
        {children}
      </h1>
    ),
    
    // H2 - Section headers in electric blue
    h2: ({ children }) => {
      const text = String(children);
      const isPositive = text.toLowerCase().includes('positif') || 
                         text.toLowerCase().includes('forces') ||
                         text.toLowerCase().includes('avantages');
      const isNegative = text.toLowerCase().includes('attention') || 
                         text.toLowerCase().includes('risques') ||
                         text.toLowerCase().includes('faiblesses') ||
                         text.toLowerCase().includes('vigilance');
      const isRecommendation = text.toLowerCase().includes('recommandation') ||
                               text.toLowerCase().includes('conclusion');
      
      return (
        <h2 className="text-xl font-bold text-[#00ff88] mt-8 mb-4 flex items-center gap-3">
          {children}
          {isPositive && (
            <span className="text-xs px-2 py-1 rounded-full bg-[#00ff88]/20 text-[#00ff88] font-medium">
              Points Forts
            </span>
          )}
          {isNegative && (
            <span className="text-xs px-2 py-1 rounded-full bg-[#ff4757]/20 text-[#ff4757] font-medium">
              À surveiller
            </span>
          )}
          {isRecommendation && (
            <span className="text-xs px-2 py-1 rounded-full bg-[#00ff88]/20 text-[#00ff88] font-medium">
              Verdict
            </span>
          )}
        </h2>
      );
    },
    
    // H3 - Subsections
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold text-white mt-6 mb-3 border-l-2 border-[#00ff88] pl-3">
        {children}
      </h3>
    ),
    
    // H4
    h4: ({ children }) => (
      <h4 className="text-base font-semibold text-gray-200 mt-4 mb-2">
        {children}
      </h4>
    ),
    
    // Paragraphs
    p: ({ children }) => {
      const text = String(children);
      
      // Check for BUY/SELL/HOLD recommendations
      if (text.includes('BUY') || text.includes('ACHETER') || text.includes('ACHAT')) {
        return (
          <p className="text-gray-300 leading-relaxed mb-4">
            {text.split(/(BUY|ACHETER|ACHAT)/gi).map((part, i) => 
              ['BUY', 'ACHETER', 'ACHAT'].includes(part.toUpperCase()) ? (
                <span key={i} className="inline-flex items-center px-3 py-1 rounded-full bg-[#00ff88]/20 text-[#00ff88] font-bold mx-1">
                  {part}
                </span>
              ) : part
            )}
          </p>
        );
      }
      
      if (text.includes('SELL') || text.includes('VENDRE') || text.includes('VENTE')) {
        return (
          <p className="text-gray-300 leading-relaxed mb-4">
            {text.split(/(SELL|VENDRE|VENTE)/gi).map((part, i) => 
              ['SELL', 'VENDRE', 'VENTE'].includes(part.toUpperCase()) ? (
                <span key={i} className="inline-flex items-center px-3 py-1 rounded-full bg-[#ff4757]/20 text-[#ff4757] font-bold mx-1">
                  {part}
                </span>
              ) : part
            )}
          </p>
        );
      }
      
      if (text.includes('HOLD') || text.includes('CONSERVER') || text.includes('NEUTRE')) {
        return (
          <p className="text-gray-300 leading-relaxed mb-4">
            {text.split(/(HOLD|CONSERVER|NEUTRE)/gi).map((part, i) => 
              ['HOLD', 'CONSERVER', 'NEUTRE'].includes(part.toUpperCase()) ? (
                <span key={i} className="inline-flex items-center px-3 py-1 rounded-full bg-[#ffa502]/20 text-[#ffa502] font-bold mx-1">
                  {part}
                </span>
              ) : part
            )}
          </p>
        );
      }
      
      return <p className="text-gray-300 leading-relaxed mb-4">{children}</p>;
    },
    
    // Unordered lists - detect positive/negative context
    ul: ({ children }) => (
      <ul className="space-y-2 mb-4 ml-1">
        {children}
      </ul>
    ),
    
    // List items with colored bullets
    li: ({ children }) => {
      const text = String(children);
      const isPositive = text.toLowerCase().includes('croissance') ||
                         text.toLowerCase().includes('solide') ||
                         text.toLowerCase().includes('excellente') ||
                         text.toLowerCase().includes('forte') ||
                         text.toLowerCase().includes('bonne') ||
                         text.toLowerCase().includes('avantage') ||
                         text.toLowerCase().includes('opportunité');
      const isNegative = text.toLowerCase().includes('risque') ||
                         text.toLowerCase().includes('faible') ||
                         text.toLowerCase().includes('dette') ||
                         text.toLowerCase().includes('attention') ||
                         text.toLowerCase().includes('vigilance') ||
                         text.toLowerCase().includes('préoccup');
      
      return (
        <li className="flex items-start gap-2">
          <span className={`mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
            isPositive ? 'bg-[#00ff88]' : 
            isNegative ? 'bg-[#ff4757]' : 
            'bg-[#00ff88]'
          }`} />
          <span className="text-gray-300">{children}</span>
        </li>
      );
    },
    
    // Ordered lists
    ol: ({ children }) => (
      <ol className="space-y-2 mb-4 ml-1 list-decimal list-inside">
        {children}
      </ol>
    ),
    
    // Strong text
    strong: ({ children }) => (
      <strong className="font-semibold text-white">{children}</strong>
    ),
    
    // Emphasis
    em: ({ children }) => (
      <em className="italic text-gray-200">{children}</em>
    ),
    
    // Links
    a: ({ href, children }) => (
      <a 
        href={href} 
        className="text-[#00ff88] hover:text-[#00ff88]/80 underline underline-offset-2 transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    
    // Blockquotes
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-[#00ff88] bg-[#1a1a2e] pl-4 py-3 my-4 rounded-r-lg">
        <div className="text-gray-300 italic">{children}</div>
      </blockquote>
    ),
    
    // Code inline
    code: ({ inline, children }) => {
      if (inline) {
        return (
          <code className="px-1.5 py-0.5 bg-[#1a1a2e] text-[#00ff88] rounded text-sm font-mono">
            {children}
          </code>
        );
      }
      return (
        <code className="block bg-[#1a1a2e] p-4 rounded-lg text-gray-300 font-mono text-sm overflow-x-auto my-4">
          {children}
        </code>
      );
    },
    
    // Pre blocks
    pre: ({ children }) => (
      <pre className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg p-4 overflow-x-auto my-4">
        {children}
      </pre>
    ),
    
    // Tables
    table: ({ children }) => (
      <div className="overflow-x-auto my-6">
        <table className="w-full border-collapse bg-[#1a1a2e] rounded-lg overflow-hidden">
          {children}
        </table>
      </div>
    ),
    
    thead: ({ children }) => (
      <thead className="bg-[#00ff88]/10">
        {children}
      </thead>
    ),
    
    th: ({ children }) => (
      <th className="px-4 py-3 text-left text-[#00ff88] font-semibold border-b border-[#2a2a3e]">
        {children}
      </th>
    ),
    
    tbody: ({ children }) => (
      <tbody className="divide-y divide-[#2a2a3e]">
        {children}
      </tbody>
    ),
    
    tr: ({ children }) => (
      <tr className="hover:bg-white/5 transition-colors">
        {children}
      </tr>
    ),
    
    td: ({ children }) => (
      <td className="px-4 py-3 text-gray-300 border-b border-[#2a2a3e]">
        {children}
      </td>
    ),
    
    // Horizontal rule
    hr: () => (
      <hr className="border-t border-[#2a2a3e] my-8" />
    ),
    
    // Images
    img: ({ src, alt }) => (
      <img 
        src={src} 
        alt={alt} 
        className="rounded-lg max-w-full h-auto my-4 border border-[#2a2a3e]"
      />
    ),
  };

  return (
    <div className={`valoramap-markdown ${className}`}>
      <ReactMarkdown 
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default StyledMarkdown;
